/**
 * Admin feedback dashboard — an actionable note inbox plus the latent model
 * review signals (per-finding overcall rate and reviewer agreement). The two
 * workflows intentionally live in separate tabs: notes are operational tasks;
 * review signals are aggregate observations, not reference-standard metrics.
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Activity,
  CircleDot,
  ExternalLink,
  Image as ImageIcon,
  Inbox,
  Info,
  Search,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import {
  Button,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/design-system/components/react";

import { getApiBaseUrl } from "../config/api";

// Polling at 15s is cheap server-side: the BE serves the rollups from a 60s
// cache and keeps presigned screenshot URLs stable for ~45min, so a poll costs
// one inbox Query and the thumbnail srcs don't churn.
const REFRESH_MS = 15000;
const DEFAULT_MIN_REVIEWS = 5;
const COLLAPSED_SIGNAL_ROWS = 25;

interface OverviewNote {
  fb_id: string;
  category: string;
  text: string;
  reporter_email: string;
  created_at: string;
  url: string | null;
  app_version: string | null;
  screenshot_url: string | null;
  github_issue_url: string | null;
}

interface OvercallRow {
  finding: string;
  overcall: number;
  total: number;
  rate: number;
}

interface Agreement {
  agreed: number;
  disagreed: number;
  agreement_rate: number | null;
  cases_reviewed: number;
}

interface Overview {
  notes: OverviewNote[];
  overcall: OvercallRow[];
  agreement: Agreement;
}

export type SignalSort = "overcalls" | "rate" | "reviews";

// Mirrors web-demo-backend post_feedback_digest.env_label — the demo's frontend
// host tells us which environment a note came from.
export function envLabel(url: string | null): string {
  if (!url) return "unknown";
  try {
    const host = new URL(url).hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    if (host === "demo.a2zradiology.ai") return "prod";
    if (host === "staging.demo.a2zradiology.ai") return "staging";
    if (host.endsWith(".amplifyapp.com")) {
      return host.startsWith("staging.") ? "staging" : "preview";
    }
  } catch {
    return "unknown";
  }
  return "unknown";
}

const CATEGORY_LABELS: Record<string, string> = {
  ui_wording: "UI / wording",
  model_behavior: "Model behavior",
  workflow_bug: "Workflow / bug",
  other: "Other",
};

export function categoryLabel(category: string): string {
  return (
    CATEGORY_LABELS[category] ??
    category
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

function titleCaseToken(token: string): string {
  const abbreviations: Record<string, string> = { ct: "CT", gi: "GI", ivc: "IVC" };
  return token
    .split("_")
    .filter(Boolean)
    .map((word) => abbreviations[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function findingLabel(finding: string): { name: string; group: string } {
  const parts = finding.split("/");
  const name = titleCaseToken(parts.pop() || finding);
  return { name, group: parts.map(titleCaseToken).join(" / ") || "Uncategorized" };
}

export function issueNumber(issueUrl: string | null): string | null {
  if (!issueUrl) return null;
  const match = issueUrl.match(/\/issues\/(\d+)(?:$|[/?#])/);
  return match?.[1] ?? null;
}

export function sortSignals(rows: OvercallRow[], sort: SignalSort): OvercallRow[] {
  return [...rows].sort((a, b) => {
    if (sort === "rate") return b.rate - a.rate || b.total - a.total;
    if (sort === "reviews") return b.total - a.total || b.overcall - a.overcall;
    return b.overcall - a.overcall || b.total - a.total || b.rate - a.rate;
  });
}

const pct = (r: number): string => `${Math.round(r * 100)}%`;

function SelectControl({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-40 flex-col gap-1 text-xs text-fg-muted">
      <span>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 rounded-sharp border border-border-strong bg-surface-1 px-3 text-sm text-fg focus:border-signal-stroke focus:outline-none"
      >
        {children}
      </select>
    </label>
  );
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="rounded-sharp border border-border bg-surface-1 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-fg">{value}</div>
      <div className="mt-1 text-xs text-fg-faint">{detail}</div>
    </div>
  );
}

function FeedbackInbox({ notes }: { notes: OverviewNote[] }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [environment, setEnvironment] = useState("all");
  const [failedShots, setFailedShots] = useState<Record<string, string>>({});

  const categories = useMemo(
    () => [...new Set(notes.map((note) => note.category))].sort(),
    [notes],
  );
  const environments = useMemo(
    () => [...new Set(notes.map((note) => envLabel(note.url)))].sort(),
    [notes],
  );
  const filteredNotes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return notes.filter((note) => {
      if (category !== "all" && note.category !== category) return false;
      if (environment !== "all" && envLabel(note.url) !== environment) return false;
      if (!needle) return true;
      return [note.fb_id, note.text, note.reporter_email, note.url ?? "", note.app_version ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [category, environment, notes, query]);

  return (
    <section aria-labelledby="feedback-inbox-heading" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="feedback-inbox-heading" className="text-base font-semibold">
            Feedback inbox
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            Notes submitted through the in-app feedback tool, newest first.
          </p>
        </div>
        <div className="text-xs text-fg-faint">
          Showing {filteredNotes.length} of {notes.length}
        </div>
      </div>

      <div className="grid gap-3 rounded-sharp border border-border bg-surface-1 p-4 md:grid-cols-[minmax(220px,1fr)_180px_180px]">
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>Search feedback</span>
          <span className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ID, note, reporter, page…"
              className="bg-surface pl-9"
            />
          </span>
        </label>
        <SelectControl label="Category" value={category} onChange={setCategory}>
          <option value="all">All categories</option>
          {categories.map((value) => (
            <option key={value} value={value}>
              {categoryLabel(value)}
            </option>
          ))}
        </SelectControl>
        <SelectControl label="Environment" value={environment} onChange={setEnvironment}>
          <option value="all">All environments</option>
          {environments.map((value) => (
            <option key={value} value={value}>
              {categoryLabel(value)}
            </option>
          ))}
        </SelectControl>
      </div>

      {filteredNotes.length === 0 ? (
        <div className="rounded-sharp border border-dashed border-border-strong p-8 text-center text-sm text-fg-muted">
          No feedback matches those filters.
        </div>
      ) : (
        <ul className="space-y-3">
          {filteredNotes.map((note) => {
            const shotUrl = note.screenshot_url;
            const shotFailed = shotUrl !== null && failedShots[note.fb_id] === shotUrl;
            const issue = issueNumber(note.github_issue_url);
            let reportedLocation: string | null = null;
            if (note.url) {
              try {
                const parsed = new URL(note.url);
                reportedLocation = `${parsed.hostname}${parsed.pathname}`;
              } catch {
                reportedLocation = note.url;
              }
            }

            return (
              <li
                key={note.fb_id}
                className="flex flex-col gap-4 rounded-sharp border border-border bg-surface-1 p-4 sm:flex-row"
              >
                {shotUrl && !shotFailed ? (
                  <a
                    href={shotUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 self-start"
                    aria-label={`Open ${note.fb_id} screenshot`}
                  >
                    <img
                      src={shotUrl}
                      alt={`${note.fb_id} screenshot`}
                      className="h-24 w-36 rounded-sharp border border-border object-cover transition-opacity hover:opacity-80"
                      onError={() => {
                        setFailedShots((previous) => ({ ...previous, [note.fb_id]: shotUrl }));
                      }}
                    />
                  </a>
                ) : (
                  <div className="flex h-24 w-36 shrink-0 items-center justify-center rounded-sharp border border-dashed border-border-strong text-fg-faint">
                    <ImageIcon className="size-5" aria-hidden="true" />
                    <span className="sr-only">No screenshot available</span>
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono font-semibold text-signal-text-tint">
                      {note.fb_id}
                    </span>
                    <span className="rounded-sharp border border-border bg-surface-2 px-2 py-0.5 text-fg-muted">
                      {categoryLabel(note.category)}
                    </span>
                    <span className="rounded-sharp border border-border bg-surface-2 px-2 py-0.5 text-fg-dim">
                      {envLabel(note.url)}
                    </span>
                    <span
                      className={
                        issue
                          ? "rounded-sharp border border-signal-stroke bg-signal-tint px-2 py-0.5 text-signal-text-tint"
                          : "rounded-sharp border border-dashed border-border-strong px-2 py-0.5 text-fg-muted"
                      }
                    >
                      {issue ? "Issue created" : "Awaiting issue sweep"}
                    </span>
                  </div>

                  <p className="mt-2 wrap-break-word text-sm leading-6 text-fg">{note.text}</p>

                  <dl className="mt-2 grid gap-x-6 gap-y-1 text-xs text-fg-muted sm:grid-cols-2">
                    <div className="min-w-0">
                      <dt className="inline text-fg-faint">Reporter: </dt>
                      <dd className="inline">{note.reporter_email}</dd>
                    </div>
                    <div>
                      <dt className="inline text-fg-faint">Submitted: </dt>
                      <dd className="inline" title={note.created_at}>
                        {new Date(note.created_at).toLocaleString()}
                      </dd>
                    </div>
                    {reportedLocation && (
                      <div className="min-w-0 sm:col-span-2">
                        <dt className="inline text-fg-faint">Reported from: </dt>
                        <dd className="inline break-all font-mono text-[11px]">
                          {reportedLocation}
                        </dd>
                      </div>
                    )}
                    {note.app_version && (
                      <div>
                        <dt className="inline text-fg-faint">Build: </dt>
                        <dd className="inline font-mono text-[11px]">v{note.app_version}</dd>
                      </div>
                    )}
                  </dl>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {note.url && (
                      <Button asChild size="sm" variant="outline">
                        <a href={note.url} target="_blank" rel="noreferrer">
                          <ExternalLink aria-hidden="true" />
                          Open reported page
                        </a>
                      </Button>
                    )}
                    {shotUrl && !shotFailed && (
                      <Button asChild size="sm" variant="outline">
                        <a href={shotUrl} target="_blank" rel="noreferrer">
                          <ImageIcon aria-hidden="true" />
                          View screenshot
                        </a>
                      </Button>
                    )}
                    {note.github_issue_url && (
                      <Button asChild size="sm" variant="secondary">
                        <a href={note.github_issue_url} target="_blank" rel="noreferrer">
                          <CircleDot aria-hidden="true" />
                          GitHub issue{issue ? ` #${issue}` : ""}
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ReviewSignals({ overcall, agreement }: { overcall: OvercallRow[]; agreement: Agreement }) {
  const [minimumReviews, setMinimumReviews] = useState(DEFAULT_MIN_REVIEWS);
  const [sort, setSort] = useState<SignalSort>("overcalls");
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = overcall.filter(
      (row) =>
        row.total >= minimumReviews && (!needle || row.finding.toLowerCase().includes(needle)),
    );
    return sortSignals(filtered, sort);
  }, [minimumReviews, overcall, query, sort]);
  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_SIGNAL_ROWS);
  const decided = agreement.agreed + agreement.disagreed;

  return (
    <div className="space-y-6">
      <section
        aria-labelledby="agreement-heading"
        className="grid gap-4 rounded-sharp border border-border bg-surface-1 p-5 lg:grid-cols-[220px_1fr]"
      >
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">
            Reviewer agreement
          </div>
          <div className="mt-2 text-4xl font-semibold text-fg">
            {agreement.agreement_rate === null ? "—" : pct(agreement.agreement_rate)}
          </div>
          <div className="mt-2 text-xs text-fg-faint">Across all available environments</div>
        </div>
        <div>
          <h2 id="agreement-heading" className="text-base font-semibold">
            Agreement with presented findings
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-fg-muted">
            Reviewers selected Agree {agreement.agreed} times and Disagree {agreement.disagreed}
            {" times across "}
            {decided} decided finding assessments in {agreement.cases_reviewed} cases. Skipped and
            pending findings are excluded.
          </p>
          <div className="mt-3 flex items-start gap-2 rounded-sharp border border-border bg-surface-2 p-3 text-xs leading-5 text-fg-muted">
            <Info className="mt-0.5 size-4 shrink-0 text-signal-text-tint" aria-hidden="true" />
            <span>
              This is reviewer agreement with the presented finding—not accuracy, sensitivity, or
              performance against a reference standard.
            </span>
          </div>
        </div>
      </section>

      <section aria-labelledby="overcall-heading" className="space-y-4">
        <div>
          <h2 id="overcall-heading" className="text-base font-semibold">
            Model overcall signals
          </h2>
          <p className="mt-1 max-w-4xl text-sm leading-6 text-fg-muted">
            An overcall means a reviewer judged that a surfaced finding was not present. Rates use
            all decided surfacer verdicts for that finding and exclude pending decisions.
          </p>
        </div>

        <div className="grid gap-3 rounded-sharp border border-border bg-surface-1 p-4 md:grid-cols-[minmax(220px,1fr)_180px_180px]">
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            <span>Search findings</span>
            <span className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-faint" />
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setShowAll(false);
                }}
                placeholder="Finding or anatomy…"
                className="bg-surface pl-9"
              />
            </span>
          </label>
          <SelectControl
            label="Minimum reviews"
            value={String(minimumReviews)}
            onChange={(value) => {
              setMinimumReviews(Number(value));
              setShowAll(false);
            }}
          >
            <option value="1">1 or more</option>
            <option value="3">3 or more</option>
            <option value="5">5 or more</option>
            <option value="10">10 or more</option>
          </SelectControl>
          <SelectControl
            label="Sort by"
            value={sort}
            onChange={(value) => {
              setSort(value as SignalSort);
              setShowAll(false);
            }}
          >
            <option value="overcalls">Most overcalls</option>
            <option value="rate">Highest rate</option>
            <option value="reviews">Most reviewed</option>
          </SelectControl>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-fg-faint">
          <span>
            Showing {rows.length} of {overcall.length} findings with at least {minimumReviews}
            {" review"}
            {minimumReviews === 1 ? "" : "s"}.
          </span>
          {minimumReviews < DEFAULT_MIN_REVIEWS && (
            <span className="flex items-center gap-1.5 text-fg-muted">
              <Info className="size-3.5" aria-hidden="true" />
              Findings below five reviews are marked as limited data.
            </span>
          )}
        </div>

        <div className="overflow-x-auto rounded-sharp border border-border bg-surface-1">
          {visibleRows.length === 0 ? (
            <div className="p-8 text-center text-sm text-fg-muted">
              No findings match those filters.
            </div>
          ) : (
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-surface-2 text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Finding</th>
                  <th className="px-4 py-3 text-right font-medium">Overcalls</th>
                  <th className="px-4 py-3 text-right font-medium">Reviews</th>
                  <th className="w-48 px-4 py-3 text-left font-medium">Rate</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const label = findingLabel(row.finding);
                  return (
                    <tr key={row.finding} className="border-t border-border hover:bg-surface-2/60">
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-fg">{label.name}</span>
                          {row.total < DEFAULT_MIN_REVIEWS && (
                            <span className="rounded-sharp border border-dashed border-border-strong px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-muted">
                              Limited data
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-fg-muted">{label.group}</div>
                        <div
                          className="mt-0.5 font-mono text-[10px] text-fg-faint"
                          title={row.finding}
                        >
                          {row.finding}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-fg">{row.overcall}</td>
                      <td className="px-4 py-3 text-right text-fg-muted">{row.total}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
                            <div
                              className="h-full bg-warn"
                              style={{ width: `${Math.round(row.rate * 100)}%` }}
                            />
                          </div>
                          <span className="w-10 text-right font-medium text-fg">
                            {pct(row.rate)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {rows.length > COLLAPSED_SIGNAL_ROWS && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => setShowAll((current) => !current)}>
              {showAll ? "Show fewer findings" : `Show all ${rows.length} findings`}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

export default function AdminFeedback() {
  const { data, isLoading, error, dataUpdatedAt, isFetching } = useQuery<Overview>({
    queryKey: ["admin-feedback-overview"],
    queryFn: async () => {
      const response = await axios.get(`${getApiBaseUrl()}/api/feedback/overview`);
      return response.data;
    },
    refetchInterval: REFRESH_MS,
  });

  if (isLoading) return <div className="p-6 text-fg-muted">Loading feedback…</div>;
  if (error) {
    return <div className="p-6 text-warn">Failed to load the feedback overview.</div>;
  }
  if (!data) return null;

  const issueCount = data.notes.filter((note) => note.github_issue_url).length;
  const decided = data.agreement.agreed + data.agreement.disagreed;
  const candidateReviews = data.overcall.reduce((sum, row) => sum + row.total, 0);
  const overcallCount = data.overcall.reduce((sum, row) => sum + row.overcall, 0);

  return (
    <div className="mx-auto w-full max-w-[1440px] space-y-6 p-4 text-fg sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Feedback &amp; review signals</h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-muted">
            Triage submitted feedback and inspect aggregate reviewer judgments from across the demo.
          </p>
        </div>
        <div className="text-right text-xs text-fg-faint">
          <div>{isFetching ? "Refreshing…" : "Auto-refreshes every 15 seconds"}</div>
          <div className="mt-1">
            Updated {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "—"}
          </div>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Feedback notes"
          value={data.notes.length}
          detail="Submitted through the in-app feedback tool"
        />
        <SummaryCard
          label="Issues created"
          value={issueCount}
          detail={`${data.notes.length - issueCount} awaiting the issue sweep`}
        />
        <SummaryCard
          label="Reviewer agreement"
          value={data.agreement.agreement_rate === null ? "—" : pct(data.agreement.agreement_rate)}
          detail={`${decided} decided assessments across ${data.agreement.cases_reviewed} cases`}
        />
        <SummaryCard
          label="Surfacer assessments"
          value={candidateReviews}
          detail={`${overcallCount} marked as model overcalls`}
        />
      </div>

      <div className="flex items-start gap-2 rounded-sharp border border-border bg-surface-1 px-4 py-3 text-xs leading-5 text-fg-muted">
        <Info className="mt-0.5 size-4 shrink-0 text-signal-text-tint" aria-hidden="true" />
        <span>
          Review-signal records currently have no environment marker, so agreement and overcall
          aggregates combine staging and production. Feedback notes retain their source environment.
        </span>
      </div>

      <Tabs defaultValue="inbox">
        <TabsList className="h-auto w-full justify-start gap-1 border border-border bg-surface-1 p-1 sm:w-auto">
          <TabsTrigger
            value="inbox"
            className="flex-1 gap-2 data-[state=active]:bg-surface-2 sm:flex-none"
          >
            <Inbox className="size-4" aria-hidden="true" />
            Feedback inbox ({data.notes.length})
          </TabsTrigger>
          <TabsTrigger
            value="signals"
            className="flex-1 gap-2 data-[state=active]:bg-surface-2 sm:flex-none"
          >
            <Activity className="size-4" aria-hidden="true" />
            Model review signals
          </TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-5">
          <FeedbackInbox notes={data.notes} />
        </TabsContent>
        <TabsContent value="signals" className="mt-5">
          <ReviewSignals overcall={data.overcall} agreement={data.agreement} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
